// pages/alerts/AlertsPage.jsx — SOUND-847
//
// Disputifier alert coverage, gaps and refund rate by merchant and MID.
// Plan: docs/plans/2026-10-30-alerts-coverage-page.md. Internal only (route is
// <RequireRole role="internal">; the API enforces it too).
//
// Rates come from the API (computed at read time over additive counts — never
// re-derived here). Every rule the page applies — thresholds, "days ago" from
// the data's asOf, stale programs, chip predicates, sort — is in
// utilities/alertCoverage (tested). This file renders.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BellRing, ShieldCheck, RotateCcw, Bell, FileWarning, TrendingDown, BellOff, Clock } from "lucide-react";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import RecordTable from "components/Records/RecordTable";
import EmptyState from "components/EmptyState";
import ListSearchBar from "components/Records/ListSearchBar";
import { useAlerts } from "hooks/useAlerts";
import {
  PROGRAMS,
  PROGRAM_LABELS,
  chipsFor,
  applyChips,
  chipCounts,
  sortRows,
  matchesSearch,
  rowLabel,
  rowKind,
  lastAlertDate,
  isSilent,
  presetRange,
  countBelow,
  formatDay,
  alertLoadState,
  brandBreakout,
  formatRate,
} from "utilities/alertCoverage";
import { RatePill, kpiToneFor, LinkBadge, AgoText } from "./alertsUi";
import AlertDetailModal from "./AlertDetailModal";

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const PRESETS = [7, 30, 90];
// The API's own default window is the last 30 days to the latest alert_date, so
// the 30D preset sends no dates and the first load is exactly one request.
const DEFAULT_PRESET = 30;

/** Segmented control — same construction as the analytics report builder's. */
function Seg({ value, onChange, options, label }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-border" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`whitespace-nowrap px-2.5 py-1.5 text-xs ${
            value === o.value ? "bg-brand/15 text-foreground" : "text-muted-foreground hover:bg-muted/60"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`badge cursor-pointer gap-1.5 transition-colors ${
        active ? "badge-brand" : "badge-muted hover:text-foreground"
      }`}
    >
      {label}
      <span className={`tabular-nums ${active ? "" : "text-foreground"}`}>{count}</span>
    </button>
  );
}

function TwoLine({ top, bottom, align = "right" }) {
  return (
    <div className={align === "right" ? "text-right" : ""}>
      <div className="tabular-nums">{top}</div>
      <div className="h-4 text-[11px] leading-4">{bottom}</div>
    </div>
  );
}

function BrandLine({ coverage }) {
  const parts = brandBreakout(coverage);
  if (!parts.length) return null;
  return (
    <span className="space-x-2 text-muted-foreground">
      {parts.map((b) => (
        <span key={b.key} title={b.label} className={b.low ? "font-medium text-destructive" : ""}>
          <span className="font-semibold">{b.short}</span> {formatRate(b.value)}
        </span>
      ))}
    </span>
  );
}

export default function AlertsPage() {
  const [grain, setGrain] = useState("merchant");
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [custom, setCustom] = useState(null); // { from, to } when dates are typed
  const [search, setSearch] = useState("");
  const [chips, setChips] = useState(() => new Set());
  const [sort, setSort] = useState("-alerts");
  const [selected, setSelected] = useState(null);
  const [knownAsOf, setKnownAsOf] = useState(null);

  // Query range: custom dates win; a non-default preset needs asOf (from the
  // first response); the default preset lets the API pick.
  const range = useMemo(() => {
    if (custom) return custom;
    if (preset !== DEFAULT_PRESET && knownAsOf) return presetRange(knownAsOf, preset);
    return { from: undefined, to: undefined };
  }, [custom, preset, knownAsOf]);

  const merchants = useAlerts({ ...range, grain: "merchant" });
  // MID grain is fetched when the MID view is on, or a merchant's modal needs its MIDs.
  const needMids = grain === "mid" || (selected && rowKind(selected) === "merchant");
  const mids = useAlerts({ ...range, grain: "mid", enabled: Boolean(needMids) });

  useEffect(() => {
    if (merchants.asOf) setKnownAsOf(merchants.asOf);
  }, [merchants.asOf]);
  const asOf = merchants.asOf || knownAsOf;
  const from = merchants.from || range.from;
  const to = merchants.to || range.to;

  const active = grain === "merchant" ? merchants : mids;
  // 503 = the mart has not synced to presentation PG yet: an empty state, not an error.
  const notSynced =
    alertLoadState(merchants.error) === "not-synced" || alertLoadState(active.error) === "not-synced";
  const allRows = active.rows;

  const counts = useMemo(() => chipCounts(allRows, asOf, grain), [allRows, asOf, grain]);
  const rows = useMemo(() => {
    const chipped = applyChips(allRows, chips, asOf, grain);
    return sortRows(
      chipped.filter((r) => matchesSearch(r, search)),
      sort
    );
  }, [allRows, chips, asOf, grain, search, sort]);

  const toggleChip = useCallback((key) => {
    setChips((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  const addChip = (key) =>
    setChips((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  const changeGrain = (g) => {
    setGrain(g);
    // "Not linked to CRM" has no meaning for merchants.
    setChips((prev) => {
      const next = new Set(prev);
      next.delete("unlinked");
      return next;
    });
  };

  const pickPreset = (d) => {
    setPreset(d);
    setCustom(null);
  };
  const setDate = (which, value) => {
    const next = { from, to, [which]: value };
    if (next.from && next.to && next.from <= next.to) {
      setCustom(next);
      setPreset(null);
    }
  };

  const totals = merchants.totals || {};
  const tc = totals.counts || {};
  const totalCoverage = totals.coverage?.total ?? null;
  const silentCount = allRows.filter((r) => isSilent(r, asOf)).length;
  const kpiLoading = merchants.loading;

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: grain === "merchant" ? "Merchant" : "MID",
        width: "23%",
        sortKey: "name",
        truncate: false,
        exportValue: (r) => rowLabel(r).title,
        cell: (r) => {
          const { title, subtitle } = rowLabel(r);
          return (
            <div className="min-w-0">
              <div className="flex min-w-0 items-center">
                <span className="truncate font-medium" title={title}>
                  {title}
                </span>
                <LinkBadge row={r} />
              </div>
              <div className="truncate text-[11px] leading-4 text-muted-foreground" title={subtitle}>
                {subtitle}
              </div>
            </div>
          );
        },
      },
      {
        key: "coverage",
        title: () => undefined,
        header: "Coverage",
        width: "15%",
        align: "right",
        sortKey: "coverage",
        truncate: false,
        exportValue: (r) => r.coverage?.total,
        cell: (r) => (
          <TwoLine top={<RatePill value={r.coverage?.total} />} bottom={<BrandLine coverage={r.coverage} />} />
        ),
      },
      ...PROGRAMS.map((p) => ({
        key: p,
        title: () => undefined,
        header: PROGRAM_LABELS[p],
        width: "8%",
        align: "right",
        sortKey: p,
        defaultSortDir: "desc",
        truncate: false,
        exportValue: (r) => r.counts?.[p],
        cell: (r) => {
          const last = r.lastReceived?.[p];
          return (
            <TwoLine
              top={last ? fmtInt(r.counts?.[p]) : <span className="text-muted-foreground">—</span>}
              bottom={last ? <AgoText date={last} asOf={asOf} /> : null}
            />
          );
        },
      })),
      {
        key: "chargebacks",
        title: () => undefined,
        header: "Chargebacks",
        width: "10%",
        align: "right",
        sortKey: "chargebacks",
        defaultSortDir: "desc",
        truncate: false,
        exportValue: (r) => r.counts?.chargebacks,
        cell: (r) => (
          <TwoLine
            top={fmtInt(r.counts?.chargebacks)}
            bottom={
              r.counts?.chargebacksUnmatched ? (
                <span className="text-muted-foreground">{fmtInt(r.counts.chargebacksUnmatched)} unmatched</span>
              ) : null
            }
          />
        ),
      },
      {
        key: "refundRate",
        title: () => undefined,
        header: "Refund rate",
        width: "12%",
        align: "right",
        sortKey: "refundRate",
        truncate: false,
        exportValue: (r) => r.refundRate,
        cell: (r) => (
          <TwoLine
            top={<RatePill value={r.refundRate} />}
            bottom={
              r.counts?.orderNotFound ? (
                <span className="text-muted-foreground">{fmtInt(r.counts.orderNotFound)} order not found</span>
              ) : null
            }
          />
        ),
      },
      {
        key: "lastAlert",
        title: () => undefined,
        header: "Last alert",
        width: "8%",
        align: "right",
        sortKey: "lastAlert",
        defaultSortDir: "desc",
        truncate: false,
        exportValue: (r) => lastAlertDate(r),
        cell: (r) => <AgoText date={lastAlertDate(r)} asOf={asOf} />,
      },
    ],
    [grain, asOf]
  );

  const unit = grain === "merchant" ? "merchants" : "MID rows";

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle="Disputifier alert coverage, gaps and refund rate by merchant and MID."
        meta={
          from && to
            ? [
                { label: "Range", value: `${formatDay(from)} – ${formatDay(to)}` },
                { label: "Data as of", value: formatDay(asOf) },
              ]
            : []
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Seg
              label="Date range"
              value={custom ? null : preset}
              onChange={pickPreset}
              options={PRESETS.map((d) => ({ value: d, label: `${d}D` }))}
            />
            <input
              type="date"
              className="input w-auto py-1.5 text-xs"
              aria-label="From"
              value={from || ""}
              max={asOf || undefined}
              onChange={(e) => setDate("from", e.target.value)}
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              className="input w-auto py-1.5 text-xs"
              aria-label="To"
              value={to || ""}
              max={asOf || undefined}
              onChange={(e) => setDate("to", e.target.value)}
            />
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          columns={{ base: 2, md: 3, lg: 6 }}
          items={[
            {
              label: "Coverage",
              value: totalCoverage ?? "—",
              format: totalCoverage == null ? "text" : "percent",
              tone: kpiToneFor(totalCoverage),
              hint: `${fmtInt(tc.chargebacksUnmatched)} chargebacks without an alert`,
              loading: kpiLoading,
              icon: <ShieldCheck />,
            },
            {
              label: "Refund rate",
              value: totals.refundRate ?? "—",
              format: totals.refundRate == null ? "text" : "percent",
              tone: kpiToneFor(totals.refundRate),
              hint: `of ${fmtInt(tc.alerts)} alerts`,
              loading: kpiLoading,
              icon: <RotateCcw />,
            },
            {
              label: "Alerts",
              value: tc.alerts || 0,
              tone: "brand",
              hint: "Ethoca, CDRN, RDR",
              loading: kpiLoading,
              icon: <Bell />,
            },
            {
              label: "TC40 notices",
              value: tc.tc40 || 0,
              tone: "neutral",
              hint: `${fmtInt(tc.chargebacks)} chargebacks`,
              loading: kpiLoading,
              icon: <FileWarning />,
            },
            {
              // Always merchant-level: coverage is only complete there.
              label: "Merchants below 85%",
              value: countBelow(merchants.rows),
              tone: countBelow(merchants.rows) > 0 ? "destructive" : "success",
              hint: `of ${fmtInt(merchants.rows.length)} with activity`,
              loading: kpiLoading,
              icon: <TrendingDown />,
              onClick: () => {
                changeGrain("merchant");
                addChip("coverage");
              },
            },
            {
              label: `${grain === "merchant" ? "Merchants" : "MIDs"} silent 7+ days`,
              value: silentCount,
              tone: silentCount > 0 ? "warning" : "success",
              hint: "no Ethoca, CDRN or RDR",
              loading: active.loading,
              icon: <BellOff />,
              onClick: () => addChip("silent"),
            },
          ]}
        />

        <div className="space-y-3">
          <ListSearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search merchant, MID, descriptor, CRM company"
          >
            <Seg
              label="View"
              value={grain}
              onChange={changeGrain}
              options={[
                { value: "merchant", label: "Merchants" },
                { value: "mid", label: "MIDs" },
              ]}
            />
          </ListSearchBar>
          <div className="flex flex-wrap items-center gap-2">
            {chipsFor(grain).map((c) => (
              <FilterChip
                key={c.key}
                label={c.label}
                count={active.loading ? "…" : counts[c.key] ?? 0}
                active={chips.has(c.key)}
                onClick={() => toggleChip(c.key)}
              />
            ))}
            {chips.size > 0 && (
              <button type="button" className="btn btn-ghost px-2 py-1 text-xs" onClick={() => setChips(new Set())}>
                Clear
              </button>
            )}
          </div>
        </div>

        {notSynced ? (
          <EmptyState
            icon={Clock}
            heading="Alert data hasn't synced yet"
            description="The alert coverage mart has not reached this environment's reporting database. It appears here after the next presentation sync."
          />
        ) : (
        <RecordTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.key}
          onRowClick={setSelected}
          loading={active.loading}
          error={active.error}
          onRetry={active.refetch}
          filtered={chips.size > 0 || Boolean(search.trim())}
          sort={sort}
          onSortChange={setSort}
          noMatch={{
            icon: BellRing,
            heading: "Nothing matches",
            description: `Nothing matches these filters for ${formatDay(from)} – ${formatDay(to)}.`,
          }}
          empty={{
            icon: BellRing,
            heading: "No alert activity",
            description: "No Disputifier alerts or chargebacks in this range.",
            // The API names the cause (e.g. 503 "mart not synced") — show it.
            errorText: active.error?.response?.data?.error || "Failed to load alert coverage.",
          }}
        />
        )}

        {!active.loading && !active.error && !notSynced && (
          <div className="flex flex-col gap-1 text-xs text-muted-foreground md:flex-row md:justify-between">
            <span>
              {fmtInt(rows.length)} of {fmtInt(allRows.length)} {unit}
            </span>
            <span className="max-w-3xl md:text-right">
              Sound coverage = alerts ÷ (alerts + chargebacks not matched to an alert). Red below 85%, amber
              below 90%. TC40 is a fraud notice, so it is excluded from coverage and refund rate.
              {grain === "mid" &&
                " MID coverage is a lower bound: about 40% of chargebacks cannot be placed on a MID and count only at merchant level."}
            </span>
          </div>
        )}
      </div>

      {selected && (
        <AlertDetailModal
          row={selected}
          from={from}
          to={to}
          asOf={asOf}
          midRows={mids.rows}
          midLoading={mids.loading}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
